import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Flag, X, Check } from 'lucide-react';
import { REPORT_REASONS, ReportReason, submitReport } from '../lib/reports';

interface Props {
  testId: string;
  /** 'muted' on the light/dark app background, 'onGradient' on the purple/pink screen */
  tone?: 'muted' | 'onGradient';
}

// Small "Report this Yhuu" link + pop-up. Recipients don't need an account.
export default function ReportYhuu({ testId, tone = 'muted' }: Props) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [contact, setContact] = useState('');
  const [block, setBlock] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');

  const close = () => {
    setOpen(false);
    if (status === 'done') { setReason(null); setDetails(''); setContact(''); setBlock(false); setStatus('idle'); }
  };

  const send = async () => {
    if (!reason || status === 'sending') return;
    setStatus('sending');
    const res = await submitReport({ testId, reason, details, contact, block });
    setStatus(res.ok ? 'done' : 'error');
  };

  const field: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '13px 15px', borderRadius: 14, fontSize: 16,
    border: '1.5px solid #f3d3de', background: '#fff7fa', color: '#18181b', outline: 'none', fontFamily: 'inherit',
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-track="report_open"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer',
          padding: '8px 12px', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 700,
          color: tone === 'onGradient' ? 'rgba(255,255,255,0.75)' : '#a1a1aa',
          textDecoration: 'underline', textUnderlineOffset: 3,
        }}
      >
        <Flag size={13} /> Report this Yhuu
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
          >
            <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} onClick={close} />

            <motion.div
              className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
              style={{ maxHeight: '92vh', overflowY: 'auto', background: '#fff' }}
              initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', stiffness: 340, damping: 30 }}
            >
              <div className="relative text-center" style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)', padding: '22px 56px' }}>
                <p style={{ color: '#fff', fontWeight: 900, fontSize: 20, letterSpacing: -0.3 }}>Report this Yhuu</p>
                <button
                  onClick={close} aria-label="Close"
                  style={{ position: 'absolute', top: '50%', right: 14, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                >
                  <X size={16} color="#fff" />
                </button>
              </div>

              {status === 'done' ? (
                <div style={{ padding: '34px 24px 28px', textAlign: 'center', color: '#18181b' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <Check size={32} color="#10b981" strokeWidth={3} />
                  </div>
                  <div style={{ fontSize: 24, fontWeight: 900, letterSpacing: -0.5 }}>Report received</div>
                  <p style={{ fontSize: 15, lineHeight: 1.5, color: '#52525b', fontWeight: 600, margin: '10px 0 22px' }}>
                    Thank you. We'll review it as soon as we can. The sender won't be told who reported.
                  </p>
                  <button
                    onClick={close}
                    style={{ width: '100%', padding: 17, borderRadius: 999, border: 'none', cursor: 'pointer', background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff', fontWeight: 900, fontSize: 16 }}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div style={{ padding: '22px 22px 24px', color: '#18181b', textAlign: 'left' }}>
                  <p style={{ fontSize: 14, lineHeight: 1.5, color: '#52525b', fontWeight: 600, marginBottom: 14 }}>
                    Tell us what's wrong. Your report goes straight to the Yhuu team, and the sender won't be told who reported.
                  </p>

                  <div role="radiogroup" aria-label="Reason" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                    {REPORT_REASONS.map(r => {
                      const on = reason === r.id;
                      return (
                        <button
                          key={r.id} role="radio" aria-checked={on}
                          onClick={() => setReason(r.id)}
                          style={{
                            padding: '10px 14px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 800,
                            border: on ? '2px solid #e11d48' : '1.5px solid #f3d3de',
                            background: on ? '#fff0f4' : '#fff', color: on ? '#be123c' : '#52525b',
                          }}
                        >
                          {r.label}
                        </button>
                      );
                    })}
                  </div>

                  <textarea
                    value={details} onChange={e => setDetails(e.target.value)} maxLength={500} rows={3}
                    placeholder="What happened? (optional)"
                    style={{ ...field, resize: 'none', marginBottom: 10 }}
                  />
                  <input
                    value={contact} onChange={e => setContact(e.target.value)} maxLength={120}
                    placeholder="Email or phone, if you want a reply (optional)"
                    style={{ ...field, marginBottom: 14 }}
                  />

                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#3f3f46', marginBottom: 18 }}>
                    <input
                      type="checkbox" checked={block} onChange={e => setBlock(e.target.checked)}
                      style={{ width: 20, height: 20, marginTop: 1, accentColor: '#e11d48', flexShrink: 0 }}
                    />
                    Ask Yhuu to stop sending me messages
                  </label>

                  {status === 'error' && (
                    <p style={{ fontSize: 13, fontWeight: 700, color: '#be123c', marginBottom: 12 }}>
                      Couldn't send your report. Please check your connection and try again.
                    </p>
                  )}

                  <button
                    onClick={send} disabled={!reason || status === 'sending'}
                    style={{
                      width: '100%', padding: 18, borderRadius: 999, border: 'none', fontFamily: 'inherit', fontWeight: 900, fontSize: 16, color: '#fff',
                      background: 'linear-gradient(90deg, #e11d48, #ec4899)', cursor: reason ? 'pointer' : 'not-allowed',
                      opacity: !reason || status === 'sending' ? 0.5 : 1, boxShadow: '0 10px 24px rgba(225,29,72,0.28)',
                    }}
                  >
                    {status === 'sending' ? 'Sending…' : 'Send report'}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
